import {
  CardMembership,
  FactoryOutlined,
  FireTruckOutlined,
  HomeWork,
  HomeWorkOutlined,
  KeyOutlined,
  LocalShippingOutlined,
  MonetizationOnOutlined,
  People,
  PrecisionManufacturingOutlined,
  Public,
  UpcomingOutlined,
  WarehouseOutlined,
} from '@mui/icons-material';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import DomainIcon from '@mui/icons-material/Domain';
import InventoryIcon from '@mui/icons-material/Inventory';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import SettingsIcon from '@mui/icons-material/Settings';
import { usePermissions } from 'hooks/permissions/usePermissions';
import { useMemo } from 'react';

import { DashboardIcon } from '../svgIcons/DashboardIcon';
import { findRouteByPath } from './routes';

const toSubLink = href => {
  const route = findRouteByPath(href);
  return {
    id: route?.id || href,
    href,
    label: route?.label || href,
    element: route?.element || null,
    permission: route?.permission,
  };
};

// filter navigation links and their sublinks based on user permissions
export const filterNavLinks = (links, can) =>
  (links ?? []).flatMap(link => {
    if (link.subLinks) {
      const subLinks = link.subLinks.filter(subLink => can(subLink.permission));
      return subLinks.length ? [{ ...link, subLinks }] : [];
    }
    return can(link.permission) ? [link] : [];
  });

export const useNavLinks = () => {
  const { can } = usePermissions();

  const rawCategoriesLinks = [
    {
      id: '0',
      name: 'global-settings',
      icon: <Public sx={{ fontSize: 28 }} />,
      label: 'تنظیمات عمومی',
    },
    {
      id: '1',
      name: 'warehouse-management',
      icon: <WarehouseOutlined sx={{ fontSize: 28 }} />,
      label: 'مدیریت انبار',
    },
    {
      id: '2',
      name: 'warehouse-operation',
      icon: <HomeWorkOutlined sx={{ fontSize: 28 }} />,
      label: 'عملیات انبار',
    },
    {
      id: '3',
      name: 'yms-management',
      icon: <FireTruckOutlined sx={{ fontSize: 28 }} />,
      label: 'مدیریت بارگیری',
    },
    {
      id: '4',
      name: 'tms-management',
      icon: <LocalShippingOutlined sx={{ fontSize: 28 }} />,
      label: 'مدیریت حمل‌و‌نقل',
    },
    {
      id: '5',
      name: 'mom-management',
      icon: <FactoryOutlined sx={{ fontSize: 28 }} />,
      label: 'مدیریت تولید',
    },
    {
      id: '6',
      name: 'mes-management',
      icon: <PrecisionManufacturingOutlined sx={{ fontSize: 28 }} />,
      label: 'عملیات تولید',
    },
    {
      id: '7',
      name: 'procurements-management',
      icon: <UpcomingOutlined sx={{ fontSize: 28 }} />,
      label: 'تأمین',
    },
    {
      id: '8',
      name: 'sales-management',
      icon: <MonetizationOnOutlined sx={{ fontSize: 28 }} />,
      label: 'فروش',
    },
    {
      id: '9',
      name: 'key-settings',
      icon: <KeyOutlined sx={{ fontSize: 28 }} />,
      label: 'تنظیمات پایه',
    },
  ];

  // dashboard / search ===> not permission-filtered
  const sidebarActionsLinks = [
    {
      id: '1',
      icon: <DashboardIcon />,
      label: 'داشبورد',
    },
    {
      id: '2',
      icon: <SearchRoundedIcon />,
      label: 'سرچ',
    },
  ];

  // unfiltered sidebar items per category.
  // permission comes from the route.
  const rawMenuLinksMap = {
    'global-settings': [
      {
        id: '1',
        icon: <InventoryIcon />,
        label: 'مدیریت محصولات',
        subLinks: ['/', '/branchesList'].map(toSubLink),
        pathes: ['/', '/branchesList'],
      },
      {
        id: '2',
        icon: <SettingsIcon />,
        label: 'تنظیمات کدینگ',
        subLinks: [
          '/codeGroup',
          '/profileGroup',
          '/mappingProfile',
          '/carFactory',
        ].map(toSubLink),
        pathes: [
          '/codeGroup',
          '/profileGroup',
          '/mappingProfile',
          '/carFactory',
        ],
      },
      {
        id: '3',
        icon: <People />,
        label: 'مدیریت کاربران',
        subLinks: ['/users'].map(toSubLink),
        pathes: ['/users'],
      },
    ],
    'warehouse-management': [
      {
        id: '1',
        icon: <DomainIcon />,
        label: 'مدیریت مکان ها',
        subLinks: ['/locations', '/stock-take', '/WMS-reports'].map(toSubLink),
        pathes: ['/locations', '/stock-take', '/WMS-reports'],
      },
      {
        id: '2',
        icon: <CardMembership />,
        href: '/cardex',
        label: 'کاردکس',
        element: findRouteByPath('/cardex')?.element || null,
        permission: findRouteByPath('/cardex')?.permission,
      },
    ],
    'warehouse-operation': [
      {
        id: '4',
        icon: <HomeWork />,
        label: 'عملیات ها',
        subLinks: [
          '/operation/warehouse-operation-geoMap',
          '/operation/entry-list',
          '/operation/departure-list',
          '/operation/transfer-list',
          '/operation/movement-list',
          '/operation/active-PDA-list',
        ].map(toSubLink),
      },
    ],
    'yms-management': [
      {
        id: '5',
        icon: <HomeWork />,
        label: 'مدیریت بارگیری',
        subLinks: ['/YMS'].map(toSubLink),
      },
    ],
    'tms-management': [
      {
        id: '6',
        icon: <HomeWork />,
        label: 'مدیریت حمل‌و‌نقل',
        subLinks: ['/TMS'].map(toSubLink),
      },
    ],
    'mom-management': [
      {
        id: '7',
        icon: <HomeWork />,
        label: 'مدیریت تولید',
        subLinks: ['/MOM'].map(toSubLink),
      },
    ],
    'mes-management': [
      {
        id: '8',
        icon: <HomeWork />,
        label: 'عملیات تولید',
        subLinks: ['/MES'].map(toSubLink),
      },
    ],
    'procurements-management': [
      {
        id: '9',
        icon: <HomeWork />,
        label: 'تامین',
        subLinks: ['/procurements'].map(toSubLink),
      },
    ],
    'sales-management': [
      {
        id: '10',
        icon: <HomeWork />,
        label: 'فروش',
        subLinks: ['/sales'].map(toSubLink),
      },
    ],
    'key-settings': [
      {
        id: '12',
        icon: <HomeWork />,
        label: 'تنظیمات پایه',
        subLinks: ['/version-control'].map(toSubLink),
      },
      {
        id: '13',
        icon: <AdminPanelSettingsIcon />,
        label: 'سطح دسترسی',
        subLinks: ['/permissions', '/roles', '/base-roles'].map(toSubLink),
      },
    ],
  };

  // drop items and empty parent groups the user cannot access
  const menuLinksMap = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(rawMenuLinksMap).map(([key, links]) => [
          key,
          filterNavLinks(links, can),
        ]),
      ),
    [can],
  );

  // hide a category icon when its sidebar would be empty.
  const categoriesLinks = useMemo(
    () =>
      rawCategoriesLinks.filter(
        category => (menuLinksMap[category.name] ?? []).length > 0,
      ),
    [menuLinksMap],
  );

  return {
    categoriesLinks,
    sidebarActionsLinks,
    globalsNavMenuLinks: menuLinksMap['global-settings'],
    menuLinksMap,
  };
};
